import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ReportsPage() {
  const [reportType, setReportType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customerFilter, setCustomerFilter] = useState('ALL');
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [checkoutHistoryFilter, setCheckoutHistoryFilter] = useState('ALL');

  // Fetch customers when Customer Stock Analysis is selected
  React.useEffect(() => {
    if (reportType === 'customer-stock') {
      setLoadingCustomers(true);
      fetch(`${API_URL}/clients`)
        .then(res => res.json())
        .then(data => {
          setCustomers(data);
          setLoadingCustomers(false);
        })
        .catch(err => {
          console.error('Failed to load customers:', err);
          setLoadingCustomers(false);
        });
    }
  }, [reportType]);

  // Sorting function for Customer Stock Analysis
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getFilteredAndSortedItems = (items) => {
    if (!items) return items;

    // Apply checkout history filter
    let filtered = items;
    if (checkoutHistoryFilter === 'WITH_CHECKOUT') {
      filtered = items.filter(item => item.hasCheckoutHistory);
    } else if (checkoutHistoryFilter === 'WITHOUT_CHECKOUT') {
      filtered = items.filter(item => !item.hasCheckoutHistory);
    }

    // Apply sorting
    if (!sortConfig.key) return filtered;

    const sortedItems = [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle Latest Order Date sorting
      if (sortConfig.key === 'latestOrderDate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numeric sorting
      else if (sortConfig.key === 'currentQty' || sortConfig.key === 'minQty') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      // Handle string sorting (case-insensitive)
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedItems;
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setReport(null);
    setSortConfig({ key: null, direction: 'asc' });
    try {
      let url = '';
      if (reportType === 'overruns') {
        url = `${API_URL}/reports/overruns-value?startDate=${typeof startDate === 'string' ? startDate : startDate?.toISOString?.().slice(0,10) || ''}&endDate=${typeof endDate === 'string' ? endDate : endDate?.toISOString?.().slice(0,10) || ''}`;
      } else if (reportType === 'calloffs') {
        url = `${API_URL}/reports/calloffs?startDate=${typeof startDate === 'string' ? startDate : startDate?.toISOString?.().slice(0,10) || ''}&endDate=${typeof endDate === 'string' ? endDate : endDate?.toISOString?.().slice(0,10) || ''}`;
      } else if (reportType === 'customer-stock') {
        url = `${API_URL}/reports/customer-stock?customerId=${customerFilter}&colorFilter=${startDate}`;
      } else {
        setError('Please select a report type.');
        setLoading(false);
        return;
      }
      console.log('Fetching report from:', url);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch report');
      const data = await res.json();
      setReport(data);
    } catch (err) {
      setError('Failed to fetch report.');
    }
    setLoading(false);
  };

  // Export as PDF handler
  const handleExportPDF = () => {
    const doc = new jsPDF();
    let pdfTitle = reportType === 'overruns' ? 'Value of Overruns Sold' : 'Call-offs report';
    let filteredBookings = report.bookings;
    if (customerFilter && customerFilter !== 'ALL') {
      pdfTitle += `: ${customerFilter}`;
      filteredBookings = report.bookings.filter(b => b.clientName === customerFilter);
    }
    doc.text(pdfTitle, 14, 16);
    doc.setFontSize(11);
    doc.text(`Date range: ${startDate} to ${endDate}`, 14, 24);
    const columns = [
      { header: 'Work Ticket Nr', dataKey: 'orderNumber' },
      { header: 'Customer', dataKey: 'clientName' },
      { header: 'Product Code', dataKey: 'productCode' },
      { header: 'Description', dataKey: 'mainCategory' },
      { header: 'Variant', dataKey: 'variantDesc' },
      { header: 'Label Size', dataKey: 'labelSize' },
      { header: 'Qty', dataKey: 'quantity' },
      { header: 'Cost/1000', dataKey: 'costAtBooking' },
      { header: 'Value', dataKey: 'value' },
      { header: 'Note', dataKey: 'note' }
    ];
    const rows = filteredBookings.map(b => ({
      orderNumber: b.orderNumber,
      clientName: b.clientName,
      productCode: b.productCode,
      mainCategory: b.mainCategory,
      variantDesc: b.variantDesc,
      labelSize: b.labelSize,
      quantity: b.quantity,
      costAtBooking: `R ${Number(b.costAtBooking).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}`,
      value: `R ${((Number(b.costAtBooking) / 1000) * Number(b.quantity)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}`,
      note: b.note || '' // show full note in PDF
    }));
    autoTable(doc, {
      head: [columns.map(col => col.header)],
      body: rows.map(row => columns.map(col => row[col.dataKey])),
      startY: 32,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210], textColor: 255 },
      margin: { left: 8, right: 8 }
    });
    doc.save('overruns-value-report.pdf');
  };

  // Export Customer Stock Analysis as PDF
  const handleExportCustomerStockPDF = () => {
    if (!report || !report.items) return;
    
    const doc = new jsPDF();
    let pdfTitle = 'Customer Stock Analysis Report';
    
    // Add filter information to title
    const customerName = report.customers?.find(c => c.id === customerFilter)?.name || 'All Customers';
    const colorFilterName = startDate === 'RED' ? 'Red (Critical)' : 
                           startDate === 'ORANGE' ? 'Orange/Yellow (Warning)' : 
                           startDate === 'GREEN' ? 'Green (Healthy)' : 
                           startDate === 'NO_MIN' ? 'No Minimum Set' : 'All Levels';
    
    if (customerFilter !== 'ALL') pdfTitle += ` - ${customerName}`;
    if (startDate !== 'ALL') pdfTitle += ` - ${colorFilterName}`;
    
    doc.text(pdfTitle, 14, 16);
    doc.setFontSize(11);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 24);
    
    // Add summary statistics
    doc.setFontSize(12);
    doc.text('Summary Statistics:', 14, 35);
    doc.setFontSize(10);
    doc.text(`Total Items: ${report.summary?.totalItems || 0}`, 14, 42);
    doc.text(`Red (Critical): ${report.summary?.red || 0} (${report.summary?.redPercent || '0.0'}%)`, 14, 48);
    doc.text(`Orange/Yellow (Warning): ${report.summary?.orange || 0} (${report.summary?.orangePercent || '0.0'}%)`, 14, 54);
    doc.text(`Green (Healthy): ${report.summary?.green || 0} (${report.summary?.greenPercent || '0.0'}%)`, 14, 60);
    doc.text(`No Minimum Set: ${report.summary?.noMin || 0} (${report.summary?.noMinPercent || '0.0'}%)`, 14, 66);
    
    // Create table
    const columns = [
      { header: 'Customer', dataKey: 'clientName' },
      { header: 'Product Code', dataKey: 'productCode' },
      { header: 'Description', dataKey: 'description' },
      { header: 'Variant', dataKey: 'variant' },
      { header: 'Current Qty', dataKey: 'currentQty' },
      { header: 'Min Qty', dataKey: 'minQty' },
      { header: 'Latest Order Date', dataKey: 'latestOrderDate' },
      { header: 'Status', dataKey: 'status' }
    ];
    
    const rows = report.items.map(item => ({
      clientName: item.clientName,
      productCode: item.productCode,
      description: item.description,
      variant: item.variant || '-',
      currentQty: item.currentQty.toLocaleString(),
      minQty: item.minQty.toLocaleString(),
      latestOrderDate: item.latestOrderDate ? new Date(item.latestOrderDate).toLocaleDateString('en-ZA') : 'Never',
      status: item.colorGroup === 'RED' ? 'CRITICAL' :
              item.colorGroup === 'ORANGE' || item.colorGroup === 'YELLOW' ? 'WARNING' :
              item.colorGroup === 'GREEN' ? 'HEALTHY' : 'NO MIN'
    }));
    
    autoTable(doc, {
      head: [columns.map(col => col.header)],
      body: rows.map(row => columns.map(col => row[col.dataKey])),
      startY: 75,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210], textColor: 255 },
      margin: { left: 8, right: 8 }
    });
    
    doc.save('customer-stock-analysis-report.pdf');
  };

  // Export Customer Stock Analysis as CSV
  const handleExportCustomerStockCSV = () => {
    if (!report || !report.items) return;
    
    const customerName = customers.find(c => c.id === customerFilter)?.name || 'All Customers';
    const colorFilterName = startDate === 'RED' ? 'Red (Critical)' : 
                           startDate === 'ORANGE' ? 'Orange/Yellow (Warning)' : 
                           startDate === 'GREEN' ? 'Green (Healthy)' : 
                           startDate === 'NO_MIN' ? 'No Minimum Set' : 'All Levels';
    
    // Create CSV content
    let csvContent = 'Customer Stock Analysis Report\n';
    csvContent += `Generated: ${new Date().toLocaleString()}\n`;
    csvContent += `Customer Filter: ${customerName}\n`;
    csvContent += `Stock Level Filter: ${colorFilterName}\n\n`;
    
    // Add summary
    csvContent += 'SUMMARY STATISTICS\n';
    csvContent += `Total Items,${report.summary?.totalItems || 0}\n`;
    csvContent += `Red (Critical),${report.summary?.red || 0},${report.summary?.redPercent || '0.0'}%\n`;
    csvContent += `Orange/Yellow (Warning),${report.summary?.orange || 0},${report.summary?.orangePercent || '0.0'}%\n`;
    csvContent += `Green (Healthy),${report.summary?.green || 0},${report.summary?.greenPercent || '0.0'}%\n`;
    csvContent += `No Minimum Set,${report.summary?.noMin || 0},${report.summary?.noMinPercent || '0.0'}%\n\n`;
    
    // Add headers
    csvContent += 'DETAILED ITEMS\n';
    csvContent += 'Customer,Product Code,Description,Variant,Current Qty,Min Qty,Latest Order Date,Status\n';
    
    // Add data rows
    report.items.forEach(item => {
      const status = item.colorGroup === 'RED' ? 'CRITICAL' :
                     item.colorGroup === 'ORANGE' || item.colorGroup === 'YELLOW' ? 'WARNING' :
                     item.colorGroup === 'GREEN' ? 'HEALTHY' : 'NO MIN';
      const latestOrderDate = item.latestOrderDate ? new Date(item.latestOrderDate).toLocaleDateString('en-ZA') : 'Never';
      
      csvContent += `"${item.clientName}","${item.productCode}","${item.description}","${item.variant || '-'}",${item.currentQty},${item.minQty},"${latestOrderDate}","${status}"\n`;
    });
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'customer-stock-analysis-report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{padding:40, maxWidth: '1200px', margin: '40px auto'}}>
      <h2 style={{color:'#1976d2',fontWeight:800,fontSize:28,marginBottom:24, textAlign:'center'}}>Reports</h2>
      <div style={{background:'#f8fafc',padding:24,borderRadius:12,maxWidth:1200, margin:'0 auto'}}>
        <div style={{fontWeight:600,fontSize:18,marginBottom:16}}>Select report type:</div>
        <select value={reportType} onChange={e => setReportType(e.target.value)} style={{width:'100%',padding:'10px 16px',borderRadius:8,fontSize:16,border:'1px solid #1976d2',marginBottom:24}}>
          <option value="">-- Choose a report --</option>
          <option value="overruns">Value of Overruns sold</option>
          <option value="calloffs">Call-offs report</option>
          <option value="customer-stock">Customer Stock Analysis</option>
        </select>
        {reportType === 'customer-stock' && (
          <div>
            <h2 style={{marginBottom:16}}>Customer Stock Analysis</h2>
            <div style={{display:'flex',gap:16,marginBottom:16}}>
              <div>
                <label>Customer:</label>
                <select 
                  value={customerFilter} 
                  onChange={e => setCustomerFilter(e.target.value)}
                  style={{padding:'6px 14px',borderRadius:8,border:'1px solid #1976d2',fontSize:16,marginLeft:8}}
                  disabled={loadingCustomers}
                >
                  <option value="ALL">{loadingCustomers ? 'Loading customers...' : 'All Customers'}</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Stock Level:</label>
                <select 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                  style={{padding:'6px 14px',borderRadius:8,border:'1px solid #1976d2',fontSize:16,marginLeft:8}}
                >
                  <option value="ALL">All Levels</option>
                  <option value="RED">Red (Below Min)</option>
                  <option value="ORANGE">Orange/Yellow (Near Min)</option>
                  <option value="GREEN">Green (Healthy)</option>
                  <option value="NO_MIN">No Min Qty</option>
                </select>
              </div>
              <div>
                <label>Order History:</label>
                <select 
                  value={checkoutHistoryFilter} 
                  onChange={e => setCheckoutHistoryFilter(e.target.value)}
                  style={{padding:'6px 14px',borderRadius:8,border:'1px solid #1976d2',fontSize:16,marginLeft:8}}
                >
                  <option value="ALL">All Items</option>
                  <option value="WITH_CHECKOUT">With Checkout History</option>
                  <option value="WITHOUT_CHECKOUT">Never Checked Out</option>
                </select>
              </div>
              <button 
                onClick={handleGenerate} 
                disabled={loading} 
                style={{marginTop:20,padding:'8px 24px',background:'#1976d2',color:'#fff',border:'none',borderRadius:8,fontWeight:700}}
              >
                Generate Report
              </button>
            </div>
            {loading && <div>Loading...</div>}
            {error && <div style={{color:'#d32f2f',marginBottom:8}}>{error}</div>}
            {report && (
              <div>
                {/* Summary Statistics */}
                <div style={{background:'#fff',padding:20,borderRadius:12,marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.1)'}}>
                  <h3 style={{marginTop:0,color:'#1976d2'}}>Summary Statistics</h3>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:16}}>
                    <div style={{textAlign:'center',padding:12,background:'#f5f5f5',borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:700,color:'#333'}}>{report.summary?.totalItems || 0}</div>
                      <div style={{color:'#666'}}>Total Items</div>
                    </div>
                    <div style={{textAlign:'center',padding:12,background:'#ffebee',borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:700,color:'#d32f2f'}}>{report.summary?.red || 0}</div>
                      <div style={{color:'#666'}}>Red ({report.summary?.redPercent || '0.0'}%)</div>
                    </div>
                    <div style={{textAlign:'center',padding:12,background:'#fff3e0',borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:700,color:'#f57c00'}}>{report.summary?.orange || 0}</div>
                      <div style={{color:'#666'}}>Orange ({report.summary?.orangePercent || '0.0'}%)</div>
                    </div>
                    <div style={{textAlign:'center',padding:12,background:'#e8f5e8',borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:700,color:'#388e3c'}}>{report.summary?.green || 0}</div>
                      <div style={{color:'#666'}}>Green ({report.summary?.greenPercent || '0.0'}%)</div>
                    </div>
                    <div style={{textAlign:'center',padding:12,background:'#f3e5f5',borderRadius:8}}>
                      <div style={{fontSize:24,fontWeight:700,color:'#7b1fa2'}}>{report.summary?.noMin || 0}</div>
                      <div style={{color:'#666'}}>No Min ({report.summary?.noMinPercent || '0.0'}%)</div>
                    </div>
                  </div>
                </div>

                {/* Export Buttons */}
                <div style={{display:'flex', gap:12, marginBottom:20}}>
                  <button 
                    onClick={() => handleExportCustomerStockPDF()}
                    style={{
                      padding:'8px 16px',
                      background:'#1976d2',
                      color:'#fff',
                      border:'none',
                      borderRadius:8,
                      fontWeight:600,
                      cursor:'pointer'
                    }}
                  >
                    Export PDF
                  </button>
                  <button 
                    onClick={() => handleExportCustomerStockCSV()}
                    style={{
                      padding:'8px 16px',
                      background:'#388e3c',
                      color:'#fff',
                      border:'none',
                      borderRadius:8,
                      fontWeight:600,
                      cursor:'pointer'
                    }}
                  >
                    Export CSV
                  </button>
                </div>

                {/* Detailed Items Table */}
                {report.items && report.items.length > 0 && (
                  <div style={{overflowX:'auto', marginBottom: 24}}>
                    <table style={{width:'100%',borderCollapse:'collapse',minWidth:1000}}>
                      <thead>
                        <tr style={{background:'#e3eafc'}}>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('clientName')}>Customer{getSortIndicator('clientName')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('productCode')}>Product Code{getSortIndicator('productCode')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('description')}>Description{getSortIndicator('description')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('variant')}>Variant{getSortIndicator('variant')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('currentQty')}>Current Qty{getSortIndicator('currentQty')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('minQty')}>Min Qty{getSortIndicator('minQty')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('latestOrderDate')}>Latest Order Date{getSortIndicator('latestOrderDate')}</th>
                          <th style={{padding:'8px 12px',border:'1px solid #d6dbe7',cursor:'pointer',userSelect:'none'}} onClick={() => handleSort('colorGroup')}>Status{getSortIndicator('colorGroup')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredAndSortedItems(report.items).map((item, idx) => (
                          <tr key={idx} style={{
                            background: item.colorGroup === 'RED' ? '#ffebee' : 
                                       item.colorGroup === 'ORANGE' || item.colorGroup === 'YELLOW' ? '#fff3e0' :
                                       item.colorGroup === 'GREEN' ? '#e8f5e8' : '#f3e5f5'
                          }}>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{item.clientName}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',fontWeight:600}}>{item.productCode}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{item.description}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{item.variant}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'right'}}>{item.currentQty.toLocaleString()}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'right'}}>{item.minQty.toLocaleString()}</td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'center'}}>
                              {item.latestOrderDate ? (
                                <span>
                                  {new Date(item.latestOrderDate).toLocaleDateString('en-ZA')}
                                  {!item.hasCheckoutHistory && (
                                    <span style={{color:'#999',fontSize:11,marginLeft:4}}>(Created)</span>
                                  )}
                                </span>
                              ) : 'Never'}
                            </td>
                            <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'center'}}>
                              <span style={{
                                padding:'4px 8px',
                                borderRadius:4,
                                fontSize:12,
                                fontWeight:600,
                                color: item.colorGroup === 'RED' ? '#d32f2f' : 
                                       item.colorGroup === 'ORANGE' || item.colorGroup === 'YELLOW' ? '#f57c00' :
                                       item.colorGroup === 'GREEN' ? '#388e3c' : '#7b1fa2'
                              }}>
                                {item.colorGroup === 'RED' ? 'CRITICAL' :
                                 item.colorGroup === 'ORANGE' || item.colorGroup === 'YELLOW' ? 'WARNING' :
                                 item.colorGroup === 'GREEN' ? 'HEALTHY' : 'NO MIN'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {(reportType === 'overruns' || reportType === 'calloffs') && (
          <div>
            <h2 style={{marginBottom:16}}>
              {reportType === 'overruns' ? 'Value of Overruns Sold' : 'Call-offs report'}
            </h2>
            <div style={{display:'flex',gap:16,marginBottom:16}}>
              <div>
                <label>Start Date:</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div>
                <label>End Date:</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <button onClick={handleGenerate} disabled={!startDate || !endDate || loading} style={{marginTop:20,padding:'8px 24px',background:'#1976d2',color:'#fff',border:'none',borderRadius:8,fontWeight:700}}>Generate Report</button>
            </div>
            {loading && <div>Loading...</div>}
            {error && <div style={{color:'#d32f2f',marginBottom:8}}>{error}</div>}
            {Array.isArray(report?.bookings) && report.bookings.length > 0 ? (
  <div>
    {/* Customer Filter */}
    <div style={{marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12}}>
      <label style={{fontWeight: 600}}>Filter by customer:</label>
      <select
        value={customerFilter}
        onChange={e => setCustomerFilter(e.target.value)}
        style={{padding: '6px 14px', borderRadius: 8, border: '1px solid #1976d2', fontSize: 16}}
      >
        <option value="ALL">All Customers</option>
        {Array.from(new Set(report.bookings.map(b => b.clientName))).sort().map(name => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </div>
    <div style={{fontWeight:700,fontSize:20,marginBottom:16}}>
      Total Value: R {
        report.bookings
          .filter(b => customerFilter === 'ALL' || b.clientName === customerFilter)
          .reduce((sum, b) => sum + ((Number(b.costAtBooking) / 1000) * Number(b.quantity)), 0)
          .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          .replace(/,/g, ' ')
      }
    </div>
    {/* Detailed table of items/bookings */}
    <div style={{overflowX:'auto', marginBottom: 24}}>
      <table style={{width:'100%',borderCollapse:'collapse',minWidth:1000}}>
        <thead>
          <tr style={{background:'#e3eafc'}}>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Work Ticket Nr</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Customer</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Product Code</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Description</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Variant</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Label Size</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Qty</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Cost/1000</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Value</th>
            <th style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>Note</th>
          </tr>
        </thead>
        <tbody>
          {report.bookings
            .filter(b => customerFilter === 'ALL' || b.clientName === customerFilter)
            .map((b, idx) => (
              <tr key={idx} style={{background: idx % 2 === 0 ? '#f8fafc' : '#fff'}}>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.orderNumber}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.clientName}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.productCode}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.mainCategory}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.variantDesc}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.labelSize}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'right'}}>{b.quantity}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'right'}}>{b.costAtBooking ? `R${Number(b.costAtBooking).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}` : '-'}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',textAlign:'right'}}>{b.costAtBooking && b.quantity ? `R${((Number(b.costAtBooking)/1000)*Number(b.quantity)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}` : '-'}</td>
                <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>{b.note || ''}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  </div>
  ) : null}

          </div>
        )}
      </div>
    </div>
  );
}
